/**
 * header-wave.js
 * Ligne sinusoïdale décorative pour le header.
 * - Ondulation autonome (respiration d'amplitude + harmonique de repli),
 *   indépendante de la souris.
 * - Réactivité au mouvement de la souris sur toute la fenêtre, avec un
 *   effet de vitesse : un geste rapide du curseur fait vibrer la courbe.
 *
 * Utilisation :
 *   import { initHeaderWaves } from './header-wave';
 *   document.addEventListener('DOMContentLoaded', () => initHeaderWaves());
 *
 * Marquage attendu :
 *   <div class="header-wave" data-header-wave>
 *     <svg class="header-wave__svg" viewBox="0 0 1200 60" preserveAspectRatio="none" aria-hidden="true">
 *       <path class="header-wave__path"></path>
 *     </svg>
 *   </div>
 *
 * `[data-header-wave]` elements swapped in via le routeur JS (contenu
 * remplacé dans #main entre deux navigations, cf. PageManager#updateDOM)
 * ne sont jamais touchés par le DOMContentLoaded initial : il faut donc
 * réinitialiser explicitement les vagues du nouveau DOM (initHeaderWaves)
 * et détruire celles de l'ancien avant de le jeter (destroyHeaderWaves),
 * pour ne pas laisser tourner des rAF / listeners sur des noeuds détachés.
 */

// Associe chaque conteneur déjà initialisé à son instance, pour permettre
// à destroyHeaderWaves() de la retrouver et pour rendre initHeaderWave()
// idempotent si jamais appelé deux fois sur le même noeud.
const instances = new WeakMap();

export function initHeaderWave(container, userOptions) {
  if (instances.has(container)) return instances.get(container);

  const svg = container.querySelector('.header-wave__svg');
  const path = container.querySelector('.header-wave__path');
  if (!svg || !path) return null;

  const options = Object.assign(
    {
      viewBoxWidth: 1200,
      viewBoxHeight: 80,
      minWavelengths: 4, // nombre de vagues au repos / vitesse minimale de la souris
      maxWavelengths: 8, // nombre de vagues atteint lors d'un mouvement rapide
      baseAmplitude: 6, // amplitude de repos, en unités du viewBox — proportionnée à la hauteur pour un vrai relief d'onde
      hoverAmplitude: 18, // amplitude ajoutée sous l'influence de la souris
      hoverRadius: 320, // portée de l'influence de la souris (unités du viewBox)
      phaseSpeed: 0.4, // vitesse de défilement de l'onde principale, en radians/seconde
      samples: 64, // nombre de points échantillonnés le long du path

      // Ondulation autonome, indépendante de la souris.
      ambientSpeed: 0.2, // vitesse de la respiration d'amplitude, en radians/seconde
      ambientDepth: 0, // profondeur de la respiration (0 = aucune, 1 = forte)
      secondaryFactor: 1 + Math.sin(Math.random()), // poids de l'harmonique de repli, synchronisée sur la fondamentale (pas de battement)

      // Réaction à la vitesse du curseur (mouvements rapides = vibration).
      kickGain: 0.05, // sensibilité à la vitesse du curseur
      kickDecay: 0.9, // vitesse d'amortissement du "kick" (proche de 1 = decay lent)
      kickAmplitude: 8, // amplitude ajoutée au pic de vitesse
      // true : le sursaut de vitesse module l'amplitude de toute la trace,
      //   de façon uniforme — plus proche d'un oscillateur électronique.
      // false : bosse localisée près du curseur (effet "remous"), comme avant.
      uniformAmplitude: true,

      // Cycle de formes d'onde au clic sur la courbe.
      shapeSequence: ['sine', 'square', 'sawtooth', 'triangle'],

      // Tonalité jouée à chaque clic (Web Audio), assortie à la forme choisie.
      toneEnabled: true,
      toneFrequency: 220, // Hz — la note jouée (220 = La grave)
      toneDuration: 0.35, // secondes
      toneVolume: 0.05, // 0-1, volontairement discret
    },
    userOptions || {}
  );

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  const W = options.viewBoxWidth;
  const H = options.viewBoxHeight;
  const MID = H / 2;
  // Le script est la seule source de vérité pour le viewBox : plus besoin
  // de garder l'attribut du gabarit Twig synchronisé à la main.
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);

  let mouseX = W / 2;
  let lastClientX = null;
  let lastTime = null;
  let velocityKick = 0;
  let phase = 0;
  let ambientPhase = 0;
  let rafId = null;
  let isVisible = true;
  let lastFrameTime = null;
  let shapeIndex = 0;
  let audioCtx = null;

  const shapeFns = {
    sine: (angle) => Math.sin(angle),
    square: (angle) => Math.sign(Math.sin(angle)) || 1,
    sawtooth: (angle) => {
      const t = (((angle / (Math.PI * 2)) % 1) + 1) % 1;
      return 2 * t - 1;
    },
    triangle: (angle) => {
      const t = (((angle / (Math.PI * 2)) % 1) + 1) % 1;
      return t < 0.5 ? 4 * t - 1 : 3 - 4 * t;
    },
  };

  function currentShapeName() {
    return options.shapeSequence[shapeIndex % options.shapeSequence.length];
  }

  function playTone(shapeName) {
    if (!options.toneEnabled) return;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    // L'AudioContext ne peut démarrer qu'à la suite d'un vrai geste
    // utilisateur : on la crée ici, dans le gestionnaire de clic.
    if (!audioCtx) audioCtx = new AudioContextClass();
    if (audioCtx.state === 'suspended') audioCtx.resume();

    // Les quatre formes du cycle correspondent exactement aux types
    // natifs d'OscillatorNode : sine, square, sawtooth, triangle.
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = shapeName;
    osc.frequency.value = options.toneFrequency;

    const now = audioCtx.currentTime;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(options.toneVolume, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + options.toneDuration);

    osc.connect(gain).connect(audioCtx.destination);
    osc.start(now);
    osc.stop(now + options.toneDuration + 0.05);
  }

  function onClick() {
    shapeIndex = (shapeIndex + 1) % options.shapeSequence.length;
    playTone(currentShapeName());
  }

  function onMouseMove(e) {
    // Suivi sur toute la fenêtre : la courbe réagit au mouvement de la
    // souris en général, pas seulement lorsqu'elle survole le svg.
    const rect = svg.getBoundingClientRect();
    mouseX = ((e.clientX - rect.left) / rect.width) * W;

    const now = performance.now();
    if (lastTime !== null) {
      const dt = Math.max(now - lastTime, 1);
      const dx = Math.abs(e.clientX - lastClientX);
      const speed = dx / dt; // px / ms
      velocityKick = Math.min(1, velocityKick + speed * options.kickGain);
    }
    lastClientX = e.clientX;
    lastTime = now;
  }

  function drawStatic() {
    // Rendu figé pour les personnes qui préfèrent moins de mouvement :
    // une seule courbe douce, sans animation ni suivi de souris.
    const pts = [];
    for (let i = 0; i <= options.samples; i++) {
      const x = (i / options.samples) * W;
      const y =
        MID + Math.sin((x / W) * Math.PI * 2 * options.minWavelengths) * options.baseAmplitude;
      pts.push(`${x.toFixed(1)},${y.toFixed(1)}`);
    }
    path.setAttribute('d', `M${pts.join(' L')}`);
  }

  function draw(now) {
    if (lastFrameTime === null) lastFrameTime = now;
    // Delta en secondes, plafonné pour éviter un saut brusque au retour
    // sur l'onglet après une mise en veille.
    const dt = Math.min((now - lastFrameTime) / 1000, 0.1);
    lastFrameTime = now;

    if (!isVisible) {
      rafId = requestAnimationFrame(draw);
      return;
    }

    phase += options.phaseSpeed * dt;
    ambientPhase += options.ambientSpeed * dt;
    // Amortissement calé sur une référence 60 fps, pour un ressenti
    // identique quel que soit le taux de rafraîchissement de l'écran.
    velocityKick *= Math.pow(options.kickDecay, dt * 60);

    // Respiration d'amplitude autonome : la courbe vit même sans souris.
    const breathing = 1 + Math.sin(ambientPhase) * options.ambientDepth;
    // Le nombre de vagues suit velocityKick (0 au repos, 1 en mouvement
    // rapide) : plus de vagues quand la souris va vite, retour au minimum
    // fixé dans les options une fois qu'elle ralentit.
    const dynamicWavelengths =
      options.minWavelengths + (options.maxWavelengths - options.minWavelengths) * velocityKick;
    const shapeFn = shapeFns[currentShapeName()];
    const isSine = currentShapeName() === 'sine';

    const pts = [];
    for (let i = 0; i <= options.samples; i++) {
      const x = (i / options.samples) * W;
      // On recentre x par rapport au milieu du viewBox avant de l'utiliser
      // dans le terme de fréquence : ainsi, le centre reste le point fixe
      // quand dynamicWavelengths change, au lieu du bord gauche.
      const xCentered = x - W / 2;
      // const angle = (xCentered / W) * Math.PI * 2 * dynamicWavelengths + phase;
      const angle = (x / W) * Math.PI * 2 * dynamicWavelengths + phase;

      // Fondamentale + harmonique de repli, toutes deux calées sur la
      // même phase : elles voyagent ensemble, sans battement parasite,
      // pour un tracé net et lisible plutôt qu'un aspect "gélatineux".
      // L'harmonique n'est ajoutée que pour la sinusoïde : les autres
      // formes ont déjà leur propre caractère, pas besoin de la charger.
      const fundamental = shapeFn(angle);
      const overtone = isSine ? Math.sin(angle * 2) * options.secondaryFactor : 0;
      const base = (fundamental + overtone) * options.baseAmplitude * breathing;

      const dist = Math.abs(x - mouseX);
      const bump = options.uniformAmplitude
        ? velocityKick * (options.hoverAmplitude + options.kickAmplitude)
        : Math.exp(-(dist * dist) / (2 * options.hoverRadius * options.hoverRadius)) *
          (options.hoverAmplitude + velocityKick * options.kickAmplitude);

      const y = MID + base + shapeFn(angle) * bump;
      pts.push(`${x.toFixed(1)},${y.toFixed(1)}`);
    }
    path.setAttribute('d', `M${pts.join(' L')}`);
    rafId = requestAnimationFrame(draw);
  }

  function onVisibilityChange() {
    isVisible = document.visibilityState === 'visible';
  }

  if (prefersReducedMotion) {
    drawStatic();
    const staticInstance = { destroy() { instances.delete(container); } };
    instances.set(container, staticInstance);
    return staticInstance;
  }

  window.addEventListener('mousemove', onMouseMove, { passive: true });
  container.addEventListener('click', onClick);
  document.addEventListener('visibilitychange', onVisibilityChange);
  rafId = requestAnimationFrame(draw);

  const instance = {
    destroy() {
      cancelAnimationFrame(rafId);
      window.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('click', onClick);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (audioCtx) audioCtx.close();
      instances.delete(container);
    },
  };
  instances.set(container, instance);
  return instance;
}

/**
 * Initialise toutes les vagues présentes sous `root` (par défaut le
 * document entier). À appeler après toute injection de markup contenant
 * des `[data-header-wave]` — chargement initial ou remplacement de #main
 * par le routeur JS.
 */
export function initHeaderWaves(root = document, userOptions) {
  root.querySelectorAll('[data-header-wave]').forEach((el) => initHeaderWave(el, userOptions));
}

/**
 * Détruit les vagues présentes sous `root` (rAF, listeners) avant que ce
 * DOM ne soit jeté ou remplacé — sans quoi elles continuent de tourner
 * indéfiniment sur des noeuds détachés.
 */
export function destroyHeaderWaves(root) {
  root.querySelectorAll('[data-header-wave]').forEach((el) => {
    const instance = instances.get(el);
    if (instance) instance.destroy();
  });
}
